import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon } from '@mui/icons-material';
import api from '../../services/api';
import type { CompanyEconomyRow } from '../../types/organizationCompanyEconomy';
import type { OrganizationBudgetProgressItem } from '../../types/organizationCompanyEconomy';
import { formatAccountMetricValue } from '../../utils/accountMetricFormat';
import { formatCompaniesSectionTitle } from '../../utils/accountDashboardFocus';
import { useOrganizationDashboard } from './OrganizationDashboardContext';

interface CompanyRow {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  companyType?: string;
}

interface OrganizationCompaniesTableProps {
  companies: CompanyRow[];
  companiesLoading: boolean;
  canEdit: boolean;
  onOpenCompany: (companyId: string, companyName: string) => void;
  onAddCompany: () => void;
}

export default function OrganizationCompaniesTable({
  companies,
  companiesLoading,
  canEdit,
  onOpenCompany,
  onAddCompany
}: OrganizationCompaniesTableProps) {
  const {
    organizationId,
    accessGranted,
    metric,
    granularity,
    anchor,
    focusWindow,
    currencyCode
  } = useOrganizationDashboard();

  const [economyRows, setEconomyRows] = useState<CompanyEconomyRow[]>([]);
  const [progressItems, setProgressItems] = useState<OrganizationBudgetProgressItem[]>([]);
  const [economyLoading, setEconomyLoading] = useState(false);

  const economyVisible = accessGranted === true;
  const showRegistered = economyVisible;

  const overByCompany = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of progressItems) {
      if (!item.companyId || item.percent <= 100) continue;
      map[item.companyId] = (map[item.companyId] ?? 0) + 1;
    }
    return map;
  }, [progressItems]);

  const showPulse = economyVisible && progressItems.length > 0;

  const loadEconomy = useCallback(async () => {
    if (!economyVisible) {
      setEconomyRows([]);
      setProgressItems([]);
      return;
    }
    try {
      setEconomyLoading(true);
      const [economyRes, progressRes] = await Promise.all([
        api.getOrganizationCompanyEconomy(organizationId, { metric, granularity, anchor }),
        api.getOrganizationBudgetProgress(organizationId)
      ]);

      if (economyRes.success) {
        setEconomyRows(economyRes.data.companies ?? []);
      } else {
        setEconomyRows([]);
      }

      if (progressRes.success) {
        setProgressItems((progressRes.data ?? []) as OrganizationBudgetProgressItem[]);
      } else {
        setProgressItems([]);
      }
    } catch {
      setEconomyRows([]);
      setProgressItems([]);
    } finally {
      setEconomyLoading(false);
    }
  }, [organizationId, metric, granularity, anchor, economyVisible]);

  useEffect(() => {
    loadEconomy();
  }, [loadEconomy]);

  const economyMap = useMemo(
    () => new Map(economyRows.map((r) => [r.companyId, r])),
    [economyRows]
  );

  const sectionTitle = formatCompaniesSectionTitle(focusWindow?.label, granularity);
  const formatRegistered = (value: number) => formatAccountMetricValue(value, metric, currencyCode);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{sectionTitle}</Typography>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAddCompany}>
            Add Company
          </Button>
        )}
      </Box>

      {companiesLoading || economyLoading ? (
        <LinearProgress />
      ) : companies.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No companies found.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                {showRegistered && <TableCell align="right">Registered</TableCell>}
                {showPulse && <TableCell align="right">Over target</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => {
                const economy = economyMap.get(company.id);
                const canShowRegistered = economy?.economyVisible === true && showRegistered;
                const overCount = overByCompany[company.id] ?? 0;

                return (
                  <TableRow
                    key={company.id}
                    hover
                    sx={{ cursor: 'pointer', opacity: company.isActive ? 1 : 0.7 }}
                    onClick={() => onOpenCompany(company.id, company.name)}
                  >
                    <TableCell>
                      <Typography variant="body2">{company.name}</Typography>
                      {(economy?.companyType || company.companyType) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {economy?.companyType || company.companyType}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={company.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={company.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    {showRegistered && (
                      <TableCell align="right">
                        {canShowRegistered ? formatRegistered(economy?.registered ?? 0) : '—'}
                      </TableCell>
                    )}
                    {showPulse && (
                      <TableCell align="right">
                        {economy?.economyVisible ? (
                          <Typography
                            variant="body2"
                            color={overCount > 0 ? 'error.main' : 'text.secondary'}
                          >
                            {overCount}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => onOpenCompany(company.id, company.name)}
                        aria-label="Open company"
                      >
                        <BusinessIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
